import { useState, useMemo, useEffect, useRef } from "react";
import { Filter, ChevronDown } from "lucide-react";

const OverviewFilter = ({
  fleetStats = [],
  allFleetStats = [],
  totalVesselCount = 0,
  onFilterChange,
  currentFilters = {},
  vesselData = []
}) => {
  console.log("fleetStats", fleetStats);

  const fleets = useMemo(() => {
    return allFleetStats.map((fleet) => ({
      name: fleet.fleetName,
      count: fleet.count || 0,
      fleetId: fleet.fleetId,
    }));
  }, [allFleetStats]);

  const counts = useMemo(() => {
    const result = {
      status: { Sailing: 0, "At Port": 0, Anchorage: 0 },
      loading: { Ballast: 0, Laden: 0 },
      zone: { ECA: 0, "Non-ECA": 0 },
      weather: { Good: 0, Bad: 0 },
    };

    fleetStats.forEach((fleet) => {
      if (fleet.statusCounts) {
        result.status.Sailing += fleet.statusCounts.sailing || 0;
        result.status["At Port"] += fleet.statusCounts.atPort || 0;
        result.status.Anchorage += fleet.statusCounts.anchorage || 0;
      }
      if (fleet.zoneCounts) {
        result.zone.ECA += fleet.zoneCounts.eca || 0;
        result.zone["Non-ECA"] += fleet.zoneCounts.nonEca || 0;
      }
      if (fleet.weatherCounts) {
        result.weather.Good += fleet.weatherCounts.good || 0;
        result.weather.Bad += fleet.weatherCounts.bad || 0;
      }
    });

    vesselData.forEach((vessel) => {
      const loadCondition = vessel.status?.loadCondition?.toLowerCase();
      if (loadCondition === "ballast") result.loading.Ballast += 1;
      else if (loadCondition === "laden") result.loading.Laden += 1;
    });

    console.log("Updated counts (with vessel-based loadCondition):", result);
    return result;
  }, [fleetStats, vesselData]);

  // State for UI controls
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [selectedFleet, setSelectedFleet] = useState("ALL FLEET");

  // Initialize all filters as empty arrays (no selections by default)
  const [status, setStatus] = useState([]);
  const [loading, setLoading] = useState([]);
  const [zone, setZone] = useState([]);
  const [weather, setWeather] = useState([]);

  const fleetsWithAll = useMemo(
    () => [
      { name: "ALL FLEET", count: totalVesselCount, fleetId: null },
      ...fleets,
    ],
    [fleets, totalVesselCount]
  );

  const hasActiveFilters = useMemo(
    () =>
      status.length > 0 ||
      loading.length > 0 ||
      zone.length > 0 ||
      weather.length > 0 ||
      selectedFleet !== "ALL FLEET",
    [status, loading, zone, weather, selectedFleet]
  );

  useEffect(() => {
    const activeFilters = {
      fleetId:
        selectedFleet === "ALL FLEET"
          ? null
          : fleets.find((f) => f.name === selectedFleet)?.fleetId,
      status: status,
      loadCondition: loading,
      zone: zone,
      weather: weather,
    };
    onFilterChange(activeFilters);
  }, [selectedFleet, status, loading, zone, weather]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (type, value) => {
    const setters = {
      status: setStatus,
      loading: setLoading,
      zone: setZone,
      weather: setWeather,
    };

    const currentState = {
      status: status,
      loading: loading,
      zone: zone,
      weather: weather,
    }[type];

    // Toggle the value
    const newState = currentState.includes(value)
      ? currentState.filter((item) => item !== value)
      : [...currentState, value];

    setters[type](newState);
  };

  const renderPill = (isActive, type, text, onClick) => (
    <button
      key={text}
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition border ${
        isActive
          ? "bg-[#1a325d] text-white border-[#1a325d]"
          : "border-[#8ebdde] hover:bg-[#8ebdde]/30 text-white"
      }`}
    >
      {text}
      <span className="text-[10px] opacity-70">
        ({counts[type]?.[text] || 0})
      </span>
    </button>
  );

  return (
    <div className="relative z-10 overflow-visible">
      <div className="w-full p-2 mb-6 rounded-lg backdrop-blur-sm shadow-sm bg-gradient-to-r from-[#1a325d] to-[#27ade3] text-white text-[13px] flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap items-center gap-4 w-full">
          <div className="flex items-center gap-2">
            <Filter
              size={20}
              className={`transition-all ${
                hasActiveFilters
                  ? "text-white fill-white scale-105"
                  : "text-white opacity-70"
              }`}
            />
            <span className="font-semibold text-white drop-shadow-sm">
              Fleets:
            </span>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`px-3 py-1.5 rounded-md text-sm focus:outline-none flex items-center gap-2 text-white transition-all ${
                  selectedFleet !== "ALL FLEET"
                    ? "bg-[#27ade3]"
                    : "border border-[#8ebdde] bg-white/10"
                }`}
              >
                <span>
                  {selectedFleet}{" "}
                  <span className="text-white/90 text-[11px]">
                    {selectedFleet === "ALL FLEET"
                      ? `(${totalVesselCount})` // This shows total unfiltered count
                      : `(${
                          fleets.find((f) => f.name === selectedFleet)?.count ||
                          0
                        })`}
                  </span>
                </span>
                <ChevronDown className="w-4 h-4 text-white" />
              </button>

              {isOpen && (
                <div className="absolute z-[9999] mt-2 bg-white rounded-lg shadow-lg border border-gray-200 w-64 bg-gradient-to-tr from-white to-[#d9ebff] text-[#0a2540]">
                  <ul className="max-h-60 overflow-y-auto">
                    {fleetsWithAll.map((fleet) => (
                      <li
                        key={fleet.name}
                        className={`px-4 py-2 text-sm hover:bg-blue-50 cursor-pointer flex justify-between items-center ${
                          selectedFleet === fleet.name
                            ? "font-semibold bg-blue-100/40 text-[#1a325d]" // GREEN for selected
                            : ""
                        }`}
                        onClick={() => {
                          setSelectedFleet(fleet.name);
                          setIsOpen(false);
                        }}
                      >
                        <span>{fleet.name}</span>
                        <span className="text-xs text-gray-500">
                          ({fleet.count})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {[
            {
              label: "Status",
              key: "status",
              options: ["Sailing", "At Port", "Anchorage"],
              state: status,
            },
            {
              label: "Cargo Status",
              key: "loading",
              options: ["Ballast", "Laden"],
              state: loading,
            },
            // {
            //   label: "Weather",
            //   key: "weather",
            //   options: ["Good", "Bad"],
            //   state: weather,
            // },
            {
              label: "Zone",
              key: "zone",
              options: ["ECA", "Non-ECA"],
              state: zone,
            },
          ].map(({ label, key, options, state }) => (
            <div key={key} className="flex items-center gap-2">
              <span className="font-semibold text-white drop-shadow-sm">
                {label}:
              </span>
              <div className="flex gap-2">
                {options.map((option) => {
                  const isActive = state.includes(option);
                  return renderPill(isActive, key, option, () =>
                    handleToggle(key, option)
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverviewFilter;
